import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Chip,
    Typography,
    Divider,
    CircularProgress,
    Stack,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Checkbox,
    ListItemText,
    ListSubheader,
    FormControlLabel,
    Switch,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Alert,
    Tooltip,
    Collapse,
    IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import api from 'state/axios';
import ConfirmDialog from 'components/ConfirmDialog';


const ALGORITHMS = [
    {
        group: 'Smart',
        options: [
            { 
                value: 'auto',  
                label: 'Auto (Recommended)', 
                description: 'CP-SAT → LNS → Greedy automatically' 
            },
        ],
    },
    {
        group: 'Fast',
        options: [
            { 
                value: 'greedy', 
                label: 'Greedy', 
                description: 'Two-pass greedy, fastest, good for quick estimates' 
            },
        ],
    },
    {
        group: 'Balanced',
        options: [
            { 
                value: 'local-search', 
                label: 'Local Search',         
                description: 'Greedy + hill climbing, improves load balance' 
            },
            { 
                value: 'sa',           
                label: 'Simulated Annealing',  
                description: 'Greedy + SA, escapes local optima probabilistically' 
            },
        ],
    },
    {
        group: 'Thorough',
        options: [
            { 
                value: 'ga',  
                label: 'Genetic Algorithm',          
                description: 'Population-based global search with crossover & mutation' 
            },
            { 
                value: 'lns', 
                label: 'Large Neighbourhood Search', 
                description: 'Greedy + repeated destroy-and-repair cycles' 
            },
        ],
    },
    {
        group: 'Optimal',
        options: [
            { 
                value: 'cp-sat', 
                label: 'CP-SAT Solver', 
                description: 'Exact ILP solver - optimal but slower on large instances' 
            },
        ],
    },
];

const ENGINE_DISPLAY = {
    'greedy':       { label: 'Greedy',                     color: 'default' },
    'local-search': { label: 'Local Search',               color: 'primary' },
    'sa':           { label: 'Simulated Annealing',        color: 'primary' },
    'ga':           { label: 'Genetic Algorithm',          color: 'secondary' },
    'lns':          { label: 'Large Neighbourhood Search', color: 'secondary' },
    'cp-sat':       { label: 'CP-SAT (Optimal)',           color: 'success' },
};

const EngineChip = ({ engine }) => {
    if (!engine || engine === 'none') return null;
    const { label, color } = ENGINE_DISPLAY[engine] || { label: engine, color: 'default' };
    
    return (
        <Chip
            size="small"
            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            label={label}
            color={color}
            variant="outlined"
        />
    );
};


const STRATEGIES = [
    {
        value:       'balance_load',
        label:       'Balance workload',
        description: 'Even total assignment count per employee',
    },
    {
        value:       'balance_shift_types',
        label:       'Even shift distribution',
        description: 'Each employee gets a proportional mix of shift types',
    },
    {
        value:       'maximize_coverage',
        label:       'Maximize coverage',
        description: 'Fill as many slots as possible - pure slot fill rate',
    },
    {
        value:       'minimize_fatigue',
        label:       'Minimize fatigue',
        description: 'Prefer employees with fewer night-shift hours worked',
    },
];

const AutoAssignmentDialog = ({ 
    open, 
    onClose, 
    startDate, 
    endDate, 
    onApplied, 
    employeeIds 
}) => {
    const [shifts, setShifts]           = useState([]);
    const [selectedShifts, setSelected] = useState([]);
    const [algorithm, setAlgorithm]     = useState('auto');
    const [strategy, setStrategy]       = useState('balance_load');
    const [isReassign, setIsReassign]       = useState(false);
    const [isSkipRules, setIsSkipRules]     = useState(false);
    const [isComputing, setIsComputing]     = useState(false);
    const [isApplying, setIsApplying]       = useState(false);
    const [isUnassigning, setIsUnassigning] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [proposals, setProposals]     = useState(null);       // null = not computed yet
    const [stats, setStats]             = useState(null);
    const [engine, setEngine]           = useState(null);
    const [serverMsg, setServerMsg]     = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const toggleRow = (idx) => setExpandedRows(prev => {
        const next = new Set(prev);
        next.has(idx) ? next.delete(idx) : next.add(idx);
        return next;
    });

    /* Load active shifts once */
    useEffect(() => {
        if (!open) return;
        api.get('/shifts?active=true')
            .then(res => setShifts(res.data || []))
            .catch(() => toast.error('Failed to load shifts!'));
    }, [open]);

    const handleClose = () => {
        setProposals(null);
        setStats(null);
        setEngine(null);
        setServerMsg(null);
        setSelected([]);
        setIsReassign(false);
        setIsSkipRules(false);
        setStrategy('balance_load');
        setExpandedRows(new Set());
        onClose();
    };

    const handleCompute = async () => {
        setIsComputing(true);
        setProposals(null);
        setStats(null);
        setServerMsg(null);
        setExpandedRows(new Set());

        try {
            const res = await api.post('/auto-assignments/preview', {
                startDate,
                endDate,
                shiftIds:    selectedShifts.length ? selectedShifts : undefined,
                employeeIds: employeeIds?.length   ? employeeIds    : undefined,
                isReassign,
                algorithm,
                strategy,
                skipRules: isSkipRules,
            });
            setProposals(res.data.proposals || []);
            setStats(res.data.stats);
            setEngine(res.data.engine);
            setServerMsg(res.data.message || null);

        } catch (err) {
            toast.error(err?.response?.data?.message || 'Computation failed!');
        } finally {
            setIsComputing(false);
        }
    };

    const handleUnassignAll = () => setConfirmOpen(true);

    const doUnassignAll = async () => {
        setIsUnassigning(true);
        setProposals(null);
        setStats(null);
        setServerMsg(null);
        try {
            const res = await api.post('/auto-assignments/unassign-all', {
                startDate,
                endDate,
                shiftIds: selectedShifts.length ? selectedShifts : undefined,
            });
            toast.success(res.data.message || 'Unassigned all.');
            onApplied?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Unassignment failed!');
        } finally {
            setIsUnassigning(false);
        }
    };

    const handleRemoveEmployee = (proposalIdx, employeeId) => {
        setProposals(prev => {
            const next = prev.map((p, i) => {
                if (i !== proposalIdx) return p;
                const newEmployeeIds = p.employeeIds.filter(id => id !== employeeId);
                const newEmployees   = p.employees.filter(e => e._id !== employeeId);
                return { ...p, employeeIds: newEmployeeIds, employees: newEmployees };
            }).filter(p => p.employeeIds.length > 0);
            return next;
        });
    };

    const handleApply = async () => {
        if (!proposals?.length) return;
        setIsApplying(true);
        try {
            const res = await api.post('/auto-assignments/apply', { 
                proposals, 
                isReassign,
            });
            toast.success(res.data.message || 'Schedule applied.');
            onApplied?.();
            handleClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Apply failed!');
        } finally {
            setIsApplying(false);
        }
    };

    const dateLabel = useMemo(() => {
        if (!startDate || !endDate) return '';
        return `${format(new Date(startDate), 'dd/MM/yyyy')} - ${format(new Date(endDate), 'dd/MM/yyyy')}`;
    }, [startDate, endDate]);

    
    return (
        <>
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <AutoAwesomeIcon color="primary" />
                    Auto Schedule
                </Box>
            </DialogTitle>
            <Divider />

            <DialogContent>
                <Stack spacing={2.5}>
                    {/* Date range (read-only) */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">Period:</Typography>
                        <Chip 
                            label={dateLabel} 
                            size="small" 
                            variant="outlined" 
                        />
                        {employeeIds?.length > 0 && (
                            <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`${employeeIds.length} employees (filtered)`}
                            />
                        )}
                    </Box>

                    {/* Algorithm selector */}
                    <FormControl size="small" fullWidth disabled={isComputing}>
                        <InputLabel>Algorithm</InputLabel>
                        <Select
                            value={algorithm}
                            onChange={e => {
                                setAlgorithm(e.target.value);
                                setProposals(null);
                                setStats(null);
                                setServerMsg(null);
                            }}
                            label="Algorithm"
                        >
                            {ALGORITHMS?.flatMap(({ group, options }) => [
                                <ListSubheader key={group} sx={{ lineHeight: '28px', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase' }}>
                                    {group}
                                </ListSubheader>,
                                ...options.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} dense>
                                        <ListItemText
                                            primary={opt.label}
                                            secondary={opt.description}
                                            slotProps={{
                                                primary:   { variant: 'body2', fontWeight: algorithm === opt.value ? 600 : 400 },
                                                secondary: { variant: 'caption' },
                                            }}
                                        />
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </FormControl>

                    {/* Strategy selector */}
                    <FormControl size="small" fullWidth disabled={isComputing}>
                        <InputLabel>Scheduling strategy</InputLabel>
                        <Select
                            value={strategy}
                            onChange={e => {
                                setStrategy(e.target.value);
                                setProposals(null);
                                setStats(null);
                                setServerMsg(null);
                            }}
                            label="Scheduling strategy"
                        >
                            {STRATEGIES.map(s => (
                                <MenuItem key={s.value} value={s.value} dense>
                                    <ListItemText
                                        primary={s.label}
                                        secondary={s.description}
                                        slotProps={{
                                            primary:   { variant: 'body2', fontWeight: strategy === s.value ? 600 : 400 },
                                            secondary: { variant: 'caption' },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Shift selector */}
                    <FormControl size="small" fullWidth disabled={isComputing}>
                        <InputLabel>Shifts (empty = all)</InputLabel>
                        <Select
                            multiple
                            value={selectedShifts}
                            onChange={e => setSelected(e.target.value)}
                            label="Shifts (empty = all)"
                            renderValue={selected =>
                                shifts
                                    .filter(s => selected.includes(s._id))
                                    .map(s => s.shiftCode)
                                    .join(', ')
                            }
                        >
                            {shifts.map(s => (
                                <MenuItem key={s._id} value={s._id} dense>
                                    <Checkbox
                                        size="small"
                                        checked={selectedShifts.includes(s._id)}
                                    />
                                    <ListItemText
                                        primary={`${s.shiftCode} - ${s.shiftName || ''}`}
                                        secondary={`${s.startTime}-${s.endTime}`}
                                        slotProps={{
                                            primary: { variant: 'body2' },
                                            secondary: { variant: 'caption' },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Re-assign toggle */}
                    <Tooltip
                        title="Re-assign: replace current with a new optimized schedule."
                        placement="top"
                        arrow
                    >
                        <FormControlLabel
                            disabled={isComputing}
                            control={
                                <Switch
                                    size="small"
                                    checked={isReassign}
                                    onChange={e => {
                                        setIsReassign(e.target.checked);
                                        setProposals(null);
                                        setStats(null);
                                        setServerMsg(null);
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Re-assign existing
                                    {isReassign && (
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="warning.main"
                                            ml={1}
                                        >
                                            (will replace current assignments)
                                        </Typography>
                                    )}
                                </Typography>
                            }
                        />
                    </Tooltip>

                    {/* Skip rules toggle */}
                    <Tooltip
                        title="Skip rules: ignore labor rules (rest time, daily hours, overlap). Use with caution."
                        placement="top"
                        arrow
                    >
                        <FormControlLabel
                            disabled={isComputing}
                            control={
                                <Switch
                                    size="small"
                                    checked={isSkipRules}
                                    onChange={e => {
                                        setIsSkipRules(e.target.checked);
                                        setProposals(null);
                                        setStats(null);
                                        setServerMsg(null);
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Skip labor rules
                                    {isSkipRules && (
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="error.main"
                                            ml={1}
                                        >
                                            (rules ignored - may cause violations)
                                        </Typography>
                                    )}
                                </Typography>
                            }
                        />
                    </Tooltip>

                    {/* Results */}
                    {isComputing && (
                        <Box display="flex" alignItems="center" gap={1} py={1}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Computing optimal schedule…
                            </Typography>
                        </Box>
                    )}

                    {proposals !== null && !isComputing && (
                        <>
                            {/* Stats bar */}
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={1}
                            >
                                <EngineChip engine={engine} />
                                <Chip
                                    size="small"
                                    label={STRATEGIES.find(s => s.value === strategy)?.label ?? strategy}
                                    variant="outlined"
                                    color="info"
                                />
                                <Chip
                                    size="small"
                                    label={`${stats?.slotsFilled ?? 0} / ${stats?.slotsTotal ?? 0} slots filled`}
                                    color={stats?.slotsFilled === stats?.slotsTotal ? 'success' : 'warning'}
                                />
                                <Chip
                                    size="small"
                                    label={`${stats?.totalAssignments ?? 0} assignments`}
                                />
                                <Chip
                                    size="small"
                                    label={`${stats?.employeesUsed ?? 0} / ${stats?.employeesTotal ?? '?'} employees`}
                                />
                            </Box>

                            {proposals.length === 0 ? (
                                <Alert severity={serverMsg === 'no_eligible' ? 'warning' : 'info'}>
                                    {serverMsg === 'no_employees'
                                        ? 'No active employees found.'
                                        : serverMsg === 'no_eligible'
                                        ? `No eligible employees found for ${stats?.slotsTotal ?? 0} unassigned slot(s).`
                                        : 'All shifts are already fully scheduled.'}
                                </Alert>
                            ) : (
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{ maxHeight: 360 }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" />
                                                <TableCell>Date</TableCell>
                                                <TableCell>Shift</TableCell>
                                                <TableCell>Proposed Employees</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {proposals.map((p, idx) => (
                                                <React.Fragment key={idx}>
                                                    <TableRow hover>
                                                        <TableCell padding="checkbox">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleRow(idx)}
                                                            >
                                                                {expandedRows.has(idx)
                                                                    ? <KeyboardArrowUp fontSize="small" />
                                                                    : <KeyboardArrowDown fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {format(new Date(p.date), 'dd/MM EEE')}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {p.shiftCode}
                                                            </Typography>
                                                            {p.shiftName && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {p.shiftName}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                                                {p.employees.map(e => (
                                                                    <Chip
                                                                        key={e._id}
                                                                        size="small"
                                                                        label={e.employeeCode || e.employeeName}
                                                                        variant="outlined"
                                                                        onDelete={() => handleRemoveEmployee(idx, e._id)}
                                                                        sx={{ fontSize: '0.7rem' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: expandedRows.has(idx) ? undefined : 'none' }}>
                                                            <Collapse in={expandedRows.has(idx)} unmountOnExit>
                                                                <Box px={1} py={1}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Required: <strong>{p.required}</strong>
                                                                        {' · '}Already assigned ({p.existingEmployees?.length || 0}):
                                                                    </Typography>
                                                                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                                                        {p.existingEmployees?.length
                                                                            ? p.existingEmployees.map(e => (
                                                                                <Chip
                                                                                    key={e._id}
                                                                                    size="small"
                                                                                    label={e.employeeCode || e.employeeName}
                                                                                    color="default"
                                                                                    sx={{ fontSize: '0.7rem' }}
                                                                                />
                                                                            ))
                                                                            : <Typography variant="caption" color="text.secondary" fontStyle="italic">None</Typography>
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: 'auto' }}>
                    <Tooltip title="Clear all assignments" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={isUnassigning ? <CircularProgress size={14} /> : null}
                                disabled={isUnassigning || isComputing || isApplying || !startDate || !endDate}
                                onClick={handleUnassignAll}
                            >
                                Unassign
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={isComputing ? <CircularProgress size={14} /> : null}
                        disabled={isComputing || isApplying || isUnassigning || !startDate || !endDate}
                        onClick={handleCompute}
                    >
                        Compute
                    </Button>
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={isApplying ? <CircularProgress size={14} /> : null}
                        disabled={isApplying || isComputing || isUnassigning || !proposals?.length}
                        onClick={handleApply}
                    >
                        Apply
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>

        <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={doUnassignAll}
            title="Unassign All"
            message="Remove all assignments in this period? This cannot be undone."
            confirmText="Unassign All"
            confirmColor="error"
        />
        </>
    );
};

export default AutoAssignmentDialog;
